/**
 * CaptionOrderStack — vertical, draggable stack of tagged players that
 * controls caption-render order.
 *
 * Default order is left-to-right by face position in the photo
 * (`frame_bbox[0]`, source space) rendered top-to-bottom. Drag a card to
 * override; the override persists on `Photo.person_order`. The stack
 * drives the `{persons}` token in CaptionBuilder + IptcPanel.
 *
 * Empty / collapsed states:
 *   - No tagged faces yet → renders a small empty hint.
 *   - Override active → "Reset" ghost button is enabled.
 */

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars3Icon, StarIcon as StarOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

import { computeFaceCropStyle } from '../../../../utils/faceCrop';
import { matchConfidenceState } from '../../../../utils/matchConfidence';
import styles from './CaptionOrderStack.module.css';

function stop(e) {
  e.stopPropagation();
}

function SortableCard({
  id,
  position,
  entry,
  isSelected,
  isHovered,
  isDragActive,
  isPrimary,
  resolveUrl,
  onClick,
  onSkip,
  onTogglePrimary,
  onHoverIn,
  onHoverOut,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const face = entry.face;
  const cropStyle = computeFaceCropStyle(face, 44, 52);
  const previewSrc = face?.preview_url
    ? (resolveUrl ? resolveUrl(face.preview_url) : face.preview_url)
    : null;
  const showThumb = !!(previewSrc && cropStyle);

  const teamName = entry.face?.person?.team_name || entry.teamName || null;
  const jersey = entry.face?.person?.jersey_number || entry.jersey || null;
  const isVisiting = entry.face?.person?.is_visiting === true || entry.face?.is_visiting === true;
  const meta = [teamName, jersey != null ? `#${jersey}` : null, isVisiting ? 'visiting' : null]
    .filter(Boolean)
    .join(' · ');

  // IDENTITY confidence — "is this the player named above" — and nothing else.
  //
  // This used to read `face.score`, which is the DETECTION score ("is this
  // rectangle a face"). Those cluster in 0.85–0.99, so the bar under the name
  // sat at 95–100% on every face including the misidentified ones, agreeing
  // with the editor no matter what. `match_confidence` is the field that
  // actually answers the question the position on the card is asking. See
  // utils/matchConfidence.js for what the number is and is not.
  //
  // The detection score is NOT shown here at all. It is real information, but
  // it is information about the detector, not about the caption, and a second
  // unlabelled percentage on this card is exactly how the first one went
  // unquestioned. Focus/quality has its own honest instrument on the face grid
  // (utils/faceSharpness.js); this card is about who the person is.
  const confidence = matchConfidenceState(face, entry.manual);

  // Spread drag listeners on the whole card root so any part of it grabs.
  // PointerSensor's 4 px activation distance keeps simple clicks intact.
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        styles.card,
        isSelected && styles.cardSelected,
        isHovered && styles.cardHovered,
        isDragging && styles.cardDragging,
        isDragActive && !isDragging && styles.cardShifted,
      )}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
      onClick={onClick}
      role="button"
      tabIndex={0}
      title="Edit person"
      {...attributes}
      {...listeners}
    >
      <span className={styles.position}>{position}</span>
      {onTogglePrimary && (
        <button
          type="button"
          className={clsx(styles.starBtn, isPrimary && styles.starBtnActive)}
          onClick={(e) => { stop(e); onTogglePrimary(); }}
          onPointerDown={stop}
          aria-label={isPrimary ? 'Clear primary subject' : 'Mark as primary subject'}
          title={isPrimary ? 'Primary subject (click to clear)' : 'Mark as primary subject'}
        >
          {isPrimary ? (
            <StarSolid style={{ width: 14, height: 14 }} />
          ) : (
            <StarOutline style={{ width: 14, height: 14 }} />
          )}
        </button>
      )}
      <div className={styles.thumbBox}>
        {showThumb ? (
          <img
            className={styles.thumbImg}
            src={previewSrc}
            alt=""
            loading="lazy"
            style={cropStyle}
          />
        ) : (
          <span className={styles.thumbFallback}>
            {entry.manual ? 'MAN' : '—'}
          </span>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.name}>{entry.displayName || entry.label}</div>
        {meta && <div className={styles.meta}>{meta}</div>}
        {confidence ? (
          <div
            className={styles.confidence}
            title={
              confidence.ambiguous
                ? 'Face match confidence. The runner-up was close — check this one.'
                : 'Face match confidence (not a probability)'
            }
          >
            <div className={styles.bar}>
              <div
                className={clsx(
                  styles.barFill,
                  confidence.ambiguous && styles.barFillAmbiguous,
                )}
                style={{ width: `${confidence.pct}%` }}
              />
            </div>
            <span className={styles.pct}>{confidence.pct}%</span>
            {/* The close-call flag stands beside the number instead of being
                multiplied into it. Folding it in is what used to make a strong
                ambiguous match render lower than a weak clean one. */}
            {confidence.ambiguous && <span className={styles.close}>CLOSE</span>}
          </div>
        ) : (
          // Hand-tagged, matching never ran, or a face older than the field.
          // Drawn as a visible absence rather than omitted: an empty slot reads
          // as a layout glitch, and 0% or a full bar would both be inventions.
          <div className={styles.confidence} title="No face match — not measured">
            <span className={styles.pct}>—</span>
          </div>
        )}
        {entry.manual && <div className={styles.manualPill}>MANUAL</div>}
      </div>
      <span className={styles.handle} aria-hidden="true">
        <Bars3Icon style={{ width: 14, height: 14 }} />
      </span>
      {onSkip && (
        <button
          type="button"
          className={styles.skipBtn}
          onClick={(e) => { stop(e); onSkip(); }}
          onPointerDown={stop}
          aria-label="Remove from caption"
          title="Remove from caption"
        >
          ×
        </button>
      )}
    </div>
  );
}

export default function CaptionOrderStack({
  entries,
  selectedFaceId,
  hoveredPersonId,
  hasManualOverride,
  primarySubjectPersonId,
  resolveUrl,
  onReorder,
  onResetOrder,
  onSelectEntry,
  onSkipEntry,
  onSetPrimary,
  onHoverPerson,
  onAddFace,
  onAutoRelink,
  autoRelinkEnabled = false,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const visible = useMemo(
    () => (Array.isArray(entries) ? entries.filter((e) => !e.softDeleted) : []),
    [entries],
  );

  const ids = useMemo(() => visible.map((e) => e.person_id), [visible]);

  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  // Project the order while dragging so position numbers update live.
  const projectedIds = useMemo(() => {
    if (!activeId || !overId || activeId === overId) return ids;
    const oldIdx = ids.indexOf(activeId);
    const newIdx = ids.indexOf(overId);
    if (oldIdx === -1 || newIdx === -1) return ids;
    return arrayMove(ids, oldIdx, newIdx);
  }, [ids, activeId, overId]);

  const handleDragStart = (event) => {
    setActiveId(event.active?.id || null);
  };
  const handleDragOver = (event) => {
    setOverId(event.over?.id || null);
  };
  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(ids, oldIndex, newIndex);
    onReorder?.(reordered);
  };

  return (
    <div className={styles.stack}>
      <div className={styles.header}>
        <span className={styles.eyebrow}>Faces &amp; Caption order</span>
        <button
          type="button"
          className={styles.resetBtn}
          onClick={onResetOrder}
          disabled={!hasManualOverride}
          title={hasManualOverride ? 'Restore left-to-right order' : 'Already in spatial order'}
        >
          Reset
        </button>
      </div>

      {visible.length === 0 ? (
        <div className={styles.empty}>
          No tagged players yet. Click a face on the photo to link.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className={styles.list}>
              {visible.map((entry) => {
                const projected = projectedIds.indexOf(entry.person_id);
                return (
                  <SortableCard
                    key={entry.person_id}
                    id={entry.person_id}
                    position={(projected >= 0 ? projected : 0) + 1}
                    entry={entry}
                    isSelected={selectedFaceId && entry.face?.id === selectedFaceId}
                    isHovered={hoveredPersonId && entry.person_id === hoveredPersonId}
                    isDragActive={!!activeId}
                    isPrimary={!!primarySubjectPersonId && String(entry.person_id) === String(primarySubjectPersonId)}
                    resolveUrl={resolveUrl}
                    onClick={() => onSelectEntry?.(entry)}
                    onSkip={onSkipEntry ? () => onSkipEntry(entry) : null}
                    onTogglePrimary={onSetPrimary ? () => {
                      const nextId = String(entry.person_id) === String(primarySubjectPersonId)
                        ? null
                        : entry.person_id;
                      onSetPrimary(nextId);
                    } : null}
                    onHoverIn={() => onHoverPerson?.(entry.person_id)}
                    onHoverOut={() => onHoverPerson?.(null)}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.footerBtn}
          onClick={onAddFace}
          disabled={!onAddFace}
        >
          + Add face
        </button>
        <button
          type="button"
          className={styles.footerBtn}
          onClick={onAutoRelink}
          disabled={!autoRelinkEnabled || !onAutoRelink}
        >
          Auto-relink
        </button>
      </div>
    </div>
  );
}
